// game.js (refactored entry)
import './game/gameInit.js';
import './game/gameEvents.js';
import './game/gameUI.js';
import './ui/ReloadUI.js';

import * as THREE from 'three';
import { WorldManager } from './core/WorldManager.js';
import { ProjectileManager } from './combat/projectileManager.js';
import { ZombieManager } from './combat/zombieManager.js';
import { ThirdPersonCamera } from './player/ThirdPersonCamera.js';
import { WorldEditorCamera } from './editor/WorldEditorCamera.js';
import * as CANNON from 'cannon-es';
import { PlayerController } from './player/PlayerController.js';
import { PlayerSpawner } from './player/PlayerSpawner.js';
import Pistol from './weapons/Pistol.js';
import { ProjectilePool } from './projectiles/ProjectilePool.js';
import { TerrainInitializer } from './terrain/TerrainInitializer.js';
import { PlayerSpawnManager } from './terrain/PlayerSpawnManager.js';
import { TerrainChunk } from './terrain/TerrainChunk.js';
import { 
    WORLD_CHUNKS_ACROSS,
    CHUNK_SIZE,
    TERRAIN_PHYSICS,
    SPAWN_CONFIG,
    PLAYER_SPAWN
} from './terrain/TerrainConstants.js';
import { TerrainCollisionManager } from './collision/TerrainCollisionManager.js';
import { StructureDebug } from './terrain/StructureDebug.js';

// Import new loading system
import { createGameLoader } from './loading/GameLoader.js';
import { LOADING_STATES } from './loading/LoadingStates.js';
import { getPlatformSettings } from './settings/PlatformSettings.js';

// Import melee system
import './combat/melee/MeleeSystem.js';
import './combat/melee/PunchAttack.js';
import './skills/StrengthSkill.js';

// Import new PistolSkill and Debug UI modules
import './SkillAdapter.js';
import './ui/PistolDebugUI.js';

// Import wave system components
import { WaveManager } from './managers/WaveManager.js';
import { PowerUpDropManager } from './powerups/PowerUpDropManager.js';
import { createWaveUI, createWaveStartEffect } from './ui/WaveUI.js';

// Import IslandTypeManager
import IslandTypeManager from './terrain/IslandTypeManager.js';

// Import mobile controls
import { createInputAdapter } from './core/InputAdapter.js';

// Initialize game state first
const gameState = {
    isPaused: false,
    mode: 'singleplayer',
    hasGameStarted: false,
    editorCamera: null // Will be initialized after THREE is loaded
};

// Expose game state to window
window.gameState = gameState;

// Initialize game clock for deltaTime calculations
const clock = new THREE.Clock();

// Scene setup
const scene = new THREE.Scene();
scene.background = new THREE.Color(0x87CEEB); // Sky blue background
scene.fog = new THREE.Fog(0x87CEEB, 150, 600); // Reduced fog by increasing near value and far value

// Enhanced lighting
const ambientLight = new THREE.AmbientLight(0x404040, 1.0); // Increase ambient light intensity from 0.5 to 1.0
scene.add(ambientLight);

// Directional light with adjusted settings
const dirLight = new THREE.DirectionalLight(0xffffff, 0.8); // Keep intensity at 0.8
dirLight.position.set(100, 200, 100);
dirLight.castShadow = true;
dirLight.shadow.mapSize.width = 2048;
dirLight.shadow.mapSize.height = 2048;
dirLight.shadow.camera.near = 0.5;
dirLight.shadow.camera.far = 500;
dirLight.shadow.camera.left = -200;
dirLight.shadow.camera.right = 200;
dirLight.shadow.camera.top = 200;
dirLight.shadow.camera.bottom = -200;
// Add shadow bias to prevent shadow acne and reduce darkness
dirLight.shadow.bias = -0.0005;
// Reduce shadow darkness
dirLight.shadow.darkness = 0.5;
scene.add(dirLight);

// Add a secondary fill light to brighten up shadowed areas
const fillLight = new THREE.DirectionalLight(0xc2d1e8, 0.4); // Slight blue tint, moderate intensity
fillLight.position.set(-100, 50, -100); // Coming from opposite direction of main light
fillLight.castShadow = false; // No need for this light to cast shadows
scene.add(fillLight);

// Initialize crosshair
const crosshairElement = document.createElement('div');
crosshairElement.id = 'crosshair';
crosshairElement.style.cssText = `
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 18px;
    height: 18px;
    border: 2px solid rgba(255, 255, 255, 0.7);
    border-radius: 50%;
    z-index: 9999;
    pointer-events: none;
`;
document.body.appendChild(crosshairElement);

// Track aiming state for crosshair updates
let isPlayerAiming = false;

// Function to update crosshair based on aiming state
function updateCrosshair(aiming) {
    if (crosshairElement) {
        if (aiming) {
            // Smaller, more precise crosshair when aiming
            crosshairElement.style.width = '12px';
            crosshairElement.style.height = '12px';
            crosshairElement.style.opacity = '1.0';
            crosshairElement.style.border = '2px solid rgba(255, 255, 255, 0.9)';
            // Add glow effect when aiming
            crosshairElement.style.boxShadow = '0 0 5px rgba(255, 255, 255, 0.8)';
            // Show inner ring when aiming
            crosshairElement.classList.remove('non-aimed');
        } else {
            // Larger, more diffused crosshair when not aiming
            crosshairElement.style.width = '18px';
            crosshairElement.style.height = '18px';
            crosshairElement.style.opacity = '0.8';
            crosshairElement.style.border = '2px solid rgba(255, 255, 255, 0.7)';
            // Subtle box shadow when not aiming
            crosshairElement.style.boxShadow = '0 0 3px rgba(0, 0, 0, 0.5)';
            // Hide inner ring when not aiming
            crosshairElement.classList.add('non-aimed');
        }
    }
}

// Enhanced renderer setup
const renderer = new THREE.WebGLRenderer({ 
    antialias: true,
    logarithmicDepthBuffer: true,
    powerPreference: "high-performance"
});
renderer.setSize(window.innerWidth, window.innerHeight);
renderer.shadowMap.enabled = true;
renderer.shadowMap.type = THREE.PCFSoftShadowMap; // Softer shadows
renderer.outputEncoding = THREE.sRGBEncoding;
renderer.toneMapping = THREE.ACESFilmicToneMapping;
renderer.toneMappingExposure = 1.2; // Increased from 1.0 to 1.2 for brighter scene
renderer.sortObjects = true; // Enable manual sorting of objects
renderer.physicallyCorrectLights = true; // Enable physically correct lighting
document.body.appendChild(renderer.domElement);

// We no longer need these overlays for instant gameplay
// Remove loading overlay
const loadingOverlay = document.createElement('div');
loadingOverlay.style.display = 'none'; // Hide it completely
document.body.appendChild(loadingOverlay);

// Remove click-to-play overlay 
const clickToPlayOverlay = document.createElement('div');
clickToPlayOverlay.style.display = 'none'; // Hide it completely
document.body.appendChild(clickToPlayOverlay);

// Set up main camera with proper initialization
const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
// Initial camera position before game starts (this is fine as it's before ThirdPersonCamera)
camera.position.set(0, 10, 20);
camera.lookAt(0, 0, 0);
window.camera = camera;

// Add a refresh interceptor
window.addEventListener('beforeunload', (event) => {
    // Only add the refresh param if it's a regular page reload (not a navigation away)
    // We can identify this by checking if the event is triggered programmatically
    if (!event.isTrusted) return;
    
    // If the page is being reloaded (F5 or refresh button)
    // Set a flag in sessionStorage that will be checked on next load
    sessionStorage.setItem('pageRefreshed', 'true');
});

// Check if this is a page refresh and update URL if needed
if (sessionStorage.getItem('pageRefreshed') === 'true') {
    sessionStorage.removeItem('pageRefreshed'); // Clear the flag
    
    // Only modify URL if it's not already a forced seed from UI
    // (avoid overriding deliberate user seed choices)
    const currentUrl = new URL(window.location.href);
    if (!currentUrl.searchParams.has('_t')) { // _t is set by the WorldSeedUI when user explicitly generates
        currentUrl.searchParams.set('refresh', 'true');
        window.history.replaceState({}, '', currentUrl.toString());
    }
}

/**
 * Initializes the world seed from URL parameters or generates a random one
 */
function initializeWorldSeed() {
    const urlParams = new URLSearchParams(window.location.search);
    const seedParam = urlParams.get('seed');
    const forceParam = urlParams.get('_t'); // Check if this is a forced generation
    
    // For seed, use forced value or generate random
    if (forceParam && seedParam) {
        // Use the provided seed
        const numericSeed = Number(seedParam);
        if (!isNaN(numericSeed)) {
            window.worldSeed = numericSeed;
            console.log(`[Game] Using forced numeric seed: ${window.worldSeed}`);
        } else {
            window.worldSeed = hashString(seedParam);
            console.log(`[Game] Using forced string seed: ${seedParam} → ${window.worldSeed}`);
        }
    } else {
        // Generate a new random seed
        window.worldSeed = Math.floor(Math.random() * 1000000);
        console.log(`[Game] Generated new random seed: ${window.worldSeed}`);
    }
    
    // Update URL with the current values
    const newUrl = new URL(window.location.href);
    newUrl.searchParams.set('seed', window.worldSeed);
    
    // Only set islandType in URL if it was explicitly forced by the user (_t parameter)
    // Otherwise, ensure it's removed so initialize() picks randomly on reload/respawn
    if (forceParam) {
        const islandType = IslandTypeManager.getInstance().getType();
        newUrl.searchParams.set('islandType', islandType);
        newUrl.searchParams.delete('_t'); // Remove the force flag after applying it
    } else {
        newUrl.searchParams.delete('islandType'); // Ensure random type on next load
    }
    
    // Replace the current URL without creating a history entry
    window.history.replaceState({}, '', newUrl.toString());
    
    // Ensure seeds are synchronized
    window.currentGameSeed = window.worldSeed;
    console.log(`[Game] Global seed set to: ${window.worldSeed}`);
    
    return window.worldSeed;
}

// Helper function to hash string seeds
function hashString(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash);
}

// Check if URL has any persistent _t parameter from previous sessions
// and remove it to ensure fresh randomization on page load
function cleanupURLParameters() {
    const currentUrl = new URL(window.location);
    let needsCleanup = false;
    
    // Check for _t parameter which indicates a previously forced generation
    if (currentUrl.searchParams.has('_t')) {
        currentUrl.searchParams.delete('_t');
        needsCleanup = true;
    }
    
    // If we cleaned up parameters, update the URL
    if (needsCleanup) {
        window.history.replaceState({}, '', currentUrl.toString());
        console.log('[Game] Cleaned up URL parameters from previous sessions');
    }
}

// Clean up URL before initializing
cleanupURLParameters();

// Initialize the island type manager first
const islandManager = IslandTypeManager.getInstance();
const initialIslandType = islandManager.initialize();

// Generate a consistent seed for the entire game
const GAME_SEED = initializeWorldSeed();
console.log(`[Game] Master seed initialized: ${GAME_SEED}`);
window.currentGameSeed = GAME_SEED; // For compatibility with existing code

// Debug function to ensure seed consistency
function ensureSeedConsistency() {
    console.log(`[Game] Seed check - window.worldSeed: ${window.worldSeed}`);
    console.log(`[Game] Seed check - window.currentGameSeed: ${window.currentGameSeed}`);
    
    // Force align if different
    if (window.worldSeed !== window.currentGameSeed) {
        console.warn(`[Game] Detected seed mismatch! Synchronizing...`);
        window.currentGameSeed = window.worldSeed;
    }
    
    // Check URL for seed
    const urlParams = new URLSearchParams(window.location.search);
    const urlSeed = urlParams.get('seed');
    
    // Skip URL updates if the refresh parameter is set - this allows the refresh to take effect
    if (urlParams.get('refresh') === 'true') {
        return;
    }
    
    if (urlSeed && String(window.worldSeed) !== urlSeed) {
        console.warn(`[Game] URL seed (${urlSeed}) differs from world seed (${window.worldSeed})!`);
        
        // Update URL to match the active world seed
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.set('seed', window.worldSeed);
        window.history.replaceState({}, '', newUrl.toString());
        console.log(`[Game] Updated URL to match world seed: ${window.worldSeed}`);
    }
}

// Call this after initialization to ensure consistency
setTimeout(ensureSeedConsistency, 500);

// Initialize physics world with better settings for player movement
const world = new CANNON.World({
    gravity: new CANNON.Vec3(0, TERRAIN_PHYSICS.gravity, 0) // Increased gravity for better feel
});
world.broadphase = new CANNON.SAPBroadphase(world);
world.solver.iterations = 20; // Increased for better stability
world.defaultContactMaterial.friction = 0; // Zero friction for smoother movement
world.defaultContactMaterial.restitution = 0;
window.physicsWorld = world;

// Initialize platform settings
const platformSettings = getPlatformSettings();
console.log(`[Game] Detected platform: ${platformSettings.platform}, quality: ${platformSettings.qualityPreset}`);

// Update fog based on platform settings
scene.fog.density = platformSettings.fogDensity;

// Create managers with consistent seed
const worldManager = new WorldManager(scene);
const chunkManager = worldManager.chunkManager;
window.worldManager = worldManager;
window.chunkManager = chunkManager;

// Initialize terrain collision manager
const terrainCollision = TerrainCollisionManager.getInstance();
window.terrainCollision = terrainCollision;

// Initialize terrain with debug mode enabled
const terrainInitializer = TerrainInitializer(scene, { debug: true });

// Get terrain materials
const terrainMaterials = terrainInitializer.terrainMaterials;

// Ensure initial chunk is loaded with proper settings
const centerX = Math.floor(WORLD_CHUNKS_ACROSS / 2);
const centerZ = Math.floor(WORLD_CHUNKS_ACROSS / 2);

// Get or create the initial chunk
let initialChunk = chunkManager.getChunkAt(centerX, centerZ);
if (!initialChunk) {
    console.log(`[Game] Creating initial chunk at ${centerX},${centerZ}`);
    initialChunk = new TerrainChunk(
        centerX,
        centerZ,
        DEFAULT_TERRAIN_SETTINGS,
        terrainMaterials.getAllMaterials()
    );
    chunkManager.chunks.set(`${centerX},${centerZ}`, initialChunk);
}

// Add the chunk to the scene
if (initialChunk.mesh) {
    scene.add(initialChunk.mesh);
    chunkManager.loadedChunks.add(`${centerX},${centerZ}`);
    console.log(`[Game] Added initial chunk to scene at ${centerX},${centerZ}`);
}

// Initialize combat managers
const projectileManager = new ProjectileManager(scene);
window.projectileManager = projectileManager;

const zombieManager = new ZombieManager(scene, world);
window.zombieManager = zombieManager;

// Initialize wave and power-up systems
const waveManager = new WaveManager();
window.waveManager = waveManager;

const powerUpDropManager = new PowerUpDropManager(scene);
window.powerUpDropManager = powerUpDropManager;

// Create the game loader
const gameLoader = createGameLoader(scene, chunkManager);
window.gameLoader = gameLoader;

const projectilePool = new ProjectilePool(scene);
window.projectilePool = projectilePool;

// Initialize spawn manager with test mode for development
const spawnManager = new PlayerSpawnManager(SPAWN_CONFIG.MODE.TEST);
window.spawnManager = spawnManager;

// Initialize player variables
let playerMesh, playerBody, playerController, thirdPersonCamera;
let gameInitialized = false;

// Modify the existing gameState for instant gameplay
gameState.hasGameStarted = false; // Will be set to true immediately after player spawn

// Modified pointer lock request function to replace the existing one
function requestGamePointerLock() {
    // Only request pointer lock if:
    // 1. Game has officially started
    // 2. Not already in pointer lock
    // 3. In singleplayer mode
    // 4. Game is not paused
    if (gameState.hasGameStarted && 
        !document.pointerLockElement && 
        gameState.mode === 'singleplayer' && 
        !gameState.isPaused) {
        renderer.domElement.requestPointerLock();
    }
}

// Add event listeners for pointer lock
renderer.domElement.addEventListener('click', () => {
    if (gameState.hasGameStarted && !document.pointerLockElement) {
        requestGamePointerLock();
    }
});

// Initialize mobile controls after player and camera are set up
let inputAdapter;

// Function to spawn player only when world is ready
async function spawnPlayerWhenReady(spawnPosition) {
    const playerSpawner = new PlayerSpawner(world, chunkManager, scene);
    
    console.log("Spawning player immediately for instant gameplay");
    
    // Get terrain height at spawn point
    const finalHeight = terrainInitializer.getTerrainHeight(spawnPosition.x, spawnPosition.z) + 2;
    spawnPosition.y = finalHeight; // Just slightly above ground to prevent clipping
    
    // Spawn player at the calculated position
    const playerData = playerSpawner.spawnPlayer(spawnPosition);
    playerMesh = playerData.mesh;
    playerBody = playerData.body;
    
    // Pass scene to PlayerController
    playerController = new PlayerController(playerMesh, playerBody, scene);
    window.playerController = playerController; // Make it globally accessible
    window.playerMesh = playerMesh; // Make player mesh globally accessible
    
    // Add collision detection
    playerBody.addEventListener('collide', (e) => {
        playerController.handleCollision(e);
    });
    
    // Create third person camera
    thirdPersonCamera = new ThirdPersonCamera(camera, playerMesh, {
        distance: 7.5,             // Distance behind player
        height: 3.0,               // Height above player
        smoothing: 0.1,
        sensitivity: 0.002,
        minPitch: -Math.PI * 0.49, // Nearly full downward rotation
        maxPitch: Math.PI * 0.49,  // Nearly full upward rotation
        horizontalOffset: 2.5,     // Right-side offset
        lookAheadDistance: 5.0,    // Distance ahead to focus
        terrainBuffer: 2.0         // Height above terrain
    });
    
    // Make camera controller available globally for weapon systems
    window.cameraController = thirdPersonCamera;
    
    // Initialize camera forward vector for player controller
    const cameraForward = new THREE.Vector3();
    camera.getWorldDirection(cameraForward);
    
    // Initialize mobile controls after player and camera are ready
    inputAdapter = createInputAdapter({
        playerController: playerController,
        camera: thirdPersonCamera
    });
    inputAdapter.initialize();
    
    // Store input adapter globally for debugging
    window.inputAdapter = inputAdapter;
    
    playerController.update(0.016, cameraForward); // Initial update with small delta
    
    // Enable game controls immediately for instant gameplay
    gameState.hasGameStarted = true;
    gameInitialized = true;
    
    // Initialize the wave UI
    const waveUI = createWaveUI();
    
    // Hide wave UI initially
    document.getElementById('wave-ui-container').style.display = 'none';

    // Function to handle the initial game start countdown
    window.startGameCountdown = (callback) => {
        let countdown = 3;
        waveUI.showAnnouncement(`Starting in ${countdown}...`, 900);

        const intervalId = setInterval(() => {
            countdown--;
            if (countdown > 0) {
                waveUI.showAnnouncement(`${countdown}...`, 900);
            } else {
                clearInterval(intervalId);
                waveUI.showAnnouncement("Start!", 1000);
                // Show wave UI when game starts
                document.getElementById('wave-ui-container').style.display = 'flex';
                // Execute the callback to start the first wave
                if (callback) callback();
            }
        }, 1000);
    };
    
    // Initialize wave system with UI callbacks
    waveManager.onWaveChange = (wave, isIntermission) => {
        // Only update UI if wave > 0
        if (wave > 0) {
            waveUI.updateWaveNumber(wave);
            waveUI.updateCountdown(waveManager.getNextWaveCountdown(), isIntermission);
            
            if (!isIntermission) {
                waveUI.showAnnouncement(`Wave ${wave} Started!`);
                createWaveStartEffect();
            } else {
                waveUI.showAnnouncement(`Wave ${wave} Complete!`);
            }
        }
    };
    
    // Start the interval to update the countdown
    setInterval(() => {
        if (waveManager.isIntermission) {
            waveUI.updateCountdown(waveManager.getNextWaveCountdown(), true);
        }
    }, 1000);
    
    // Start the wave system
    waveManager.start();
    
    // Request pointer lock on first click
    renderer.domElement.addEventListener('click', function onFirstClick() {
        if (!document.pointerLockElement) {
            requestGamePointerLock();
        }
        // Remove this listener after first click
        renderer.domElement.removeEventListener('click', onFirstClick);
    });
    
    console.log("Player and camera setup complete - instant gameplay enabled");
    
    return playerController;
}

// Load initial chunks in a progressive manner
async function initializeWorld() {
    const startTime = performance.now();
    console.log("Starting progressive world loading...");
    
    try {
        // Calculate random spawn position first
        const playerSpawner = new PlayerSpawner(world, chunkManager, scene);
        const spawnPosition = playerSpawner.calculateSpawnPoint();
        
        console.log(`[Game] Calculated spawn position: ${spawnPosition.x}, ${spawnPosition.y}, ${spawnPosition.z}`);
        
        // Begin loading with the spawn position
        await gameLoader.beginLoading(spawnPosition, async () => {
            // This callback is called when minimal chunks are loaded
            
            // Spawn player at the calculated position
            await spawnPlayerWhenReady(spawnPosition);
            
            /* // Disabled initial zombie spawn - handled by WaveManager now
            // Spawn initial zombies asynchronously in the background
            setTimeout(() => {
                if (gameState.mode !== 'editor') {
                    for(let i = 0; i < 3; i++) {
                        zombieManager.spawnZombie(playerMesh);
                    }
                }
            }, 2000);
            */
            
            const endTime = performance.now();
            console.log(`Initial world load completed in ${((endTime - startTime)/1000).toFixed(2)} seconds`);
        });
        
        // After creating terrain, ensure island type is applied
        if (window.chunkManager) {
            const currentType = islandManager.getType();
            console.log(`[Game] Setting ChunkManager island type to: ${currentType} (${islandManager.getTypeName()})`);
            window.chunkManager.setIslandType(currentType);
        }
        
    } catch (e) {
        console.error("Error initializing world:", e);
    }
}

// Start the initialization immediately
initializeWorld();

// Initialize world editor mode capabilities (disabled temporarily)
let worldEditorCamera = null;
window.worldEditorModeEnabled = false; // Explicitly disable editor mode

// Function to switch to editor mode (disabled temporarily)
function switchToWorldEditorMode() {
    console.log('World Editor mode is temporarily disabled');
    return;
    
    /* Original code preserved but commented out
    if (thirdPersonCamera) thirdPersonCamera.disable();

    // Dispose of existing editor controls if any
    if (worldEditorCamera) {
        worldEditorCamera.dispose();
    }

    // Create a new editor camera with wider view
    worldEditorCamera = new WorldEditorCamera(renderer, scene);
    camera = worldEditorCamera.getCamera();
    
    // Calculate world size and center for camera positioning
    const worldSize = CHUNK_SIZE * WORLD_CHUNKS_ACROSS;
    const halfSize = worldSize / 2;
    const viewHeight = worldSize * 0.6;
    camera.position.set(halfSize, viewHeight, halfSize);
    camera.lookAt(halfSize, 0, halfSize);
    
    worldEditorCamera.enable();

    // Switch chunk manager to editor mode
    chunkManager.setWorldMode('editor');

    // Show editor UI
    document.getElementById('editorControls').style.display = 'block';

    // Ensure pointer lock is exited
    if (document.pointerLockElement) {
        document.exitPointerLock();
    }

    console.log('Switched to World Editor mode');
    */
}

function switchToSinglePlayerMode() {
    if (worldEditorCamera) {
        try {
            worldEditorCamera.dispose();
        } catch(e) {
            console.log('Error disposing editor camera:', e);
        }
    }

    // Switch back to dynamic chunk loading
    if (chunkManager) {
        chunkManager.setWorldMode('dynamic');
    }

    // Re-enable third-person camera
    if (thirdPersonCamera) {
        thirdPersonCamera.enable();
        camera = thirdPersonCamera.camera;
    }

    // Show crosshair
    const crosshair = document.getElementById('crosshair');
    if (crosshair) crosshair.style.display = 'block';

    // Only request pointer lock if game has started
    if (gameState.hasGameStarted) {
        requestGamePointerLock();
    }
    
    // Only hide cursor if in pointer lock
    if (document.pointerLockElement) {
        document.body.style.cursor = 'none';
    }

    console.log('Using Single Player mode');
}

// Modify switchGameMode to prevent editor mode
function switchGameMode(mode) {
    // Always force singleplayer mode
    if (mode === 'editor') {
        console.log('Editor mode is temporarily disabled');
        mode = 'singleplayer';
    }
    
    gameState.mode = mode;

    if (mode === 'editor') {
        // This won't execute due to the override above
        switchToWorldEditorMode();
    } else {
        switchToSinglePlayerMode();
    }

    document.getElementById('pauseMenu').style.display = 'none';
    gameState.isPaused = false;
}

// Global movement state to be passed to player controller
const moveState = {
    forward: false,
    backward: false,
    left: false,
    right: false,
    speed: 0.15  // Adjust this value to control movement speed
};

const editorControlsUI = document.getElementById('editorControls');
const heightRange = document.getElementById('heightRange');
const detailRange = document.getElementById('detailRange');
const edgeSharpnessRange = document.getElementById('edgeSharpnessRange');
const noiseScaleRange = document.getElementById('noiseScaleRange');
const mountainHeightRange = document.getElementById('mountainHeightRange');
const mountainSizeRange = document.getElementById('mountainSizeRange');

// Mode switching handlers
document.getElementById('storyMode')?.addEventListener('click', () => {
    switchGameMode('singleplayer');
});

document.getElementById('editorMode')?.addEventListener('click', () => {
    switchGameMode('editor');
});

// Update terrain on slider change
heightRange?.addEventListener('input', (e) => {
    const value = parseFloat(e.target.value);
    document.getElementById('heightValue').textContent = value;
    chunkManager.updateTerrainSettings({ amplitude: value });
});

detailRange?.addEventListener('input', (e) => {
    const value = parseFloat(e.target.value);
    document.getElementById('detailValue').textContent = value;
    chunkManager.updateTerrainSettings({ frequency: value });
});

// Add event listeners for new island shape controls
edgeSharpnessRange?.addEventListener('input', (e) => {
    const value = parseFloat(e.target.value);
    document.getElementById('edgeSharpnessValue').textContent = value;
    chunkManager.updateTerrainSettings({ edgeSharpness: value });
});

noiseScaleRange?.addEventListener('input', (e) => {
    const value = parseFloat(e.target.value);
    document.getElementById('noiseScaleValue').textContent = value;
    chunkManager.updateTerrainSettings({ noiseScale: value });
});

// Add event listeners for mountain controls
mountainHeightRange?.addEventListener('input', (e) => {
    const value = parseFloat(e.target.value);
    document.getElementById('mountainHeightValue').textContent = value;
    window.MOUNTAIN_RANGE_HEIGHT = value;
    chunkManager.updateTerrainSettings({ refresh: true }); // Force refresh
});

mountainSizeRange?.addEventListener('input', (e) => {
    const value = parseFloat(e.target.value);
    document.getElementById('mountainSizeValue').textContent = value;
    window.MOUNTAIN_RANGE_SIZE = value;
    chunkManager.updateTerrainSettings({ refresh: true }); // Force refresh
});

// Add mouse position tracking
const mouse = new THREE.Vector2();
document.addEventListener('mousemove', (event) => {
    mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
    mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;
});

// Add the raycaster for game interactions
const raycaster = new THREE.Raycaster();
const plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
const intersectPoint = new THREE.Vector3();

// Add proper mouse movement handler
document.addEventListener('mousemove', (event) => {
    if (gameState.isPaused) return;
    
    if (gameState.mode === 'singleplayer' && document.pointerLockElement) {
        thirdPersonCamera?.handleMouseMovement(event.movementX, event.movementY);
    }
});

// Add keyboard handlers
document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
        togglePauseState();
        return;
    }
    
    if (gameState.isPaused || !playerController) return; // Ignore movement keys when paused
    
    switch(e.key.toLowerCase()) {
        case 'w': moveState.forward = true; break;
        case 's': moveState.backward = true; break;
        case 'a': moveState.left = true; break;
        case 'd': moveState.right = true; break;
        case ' ':
            if (playerController.canJump()) {
                playerController.jump();
            }
            break;
    }
    
    // Set movement state in controller
    playerController.setMovementState(moveState);
});

document.addEventListener('keyup', (e) => {
    if (!playerController) return;
    
    switch(e.key.toLowerCase()) {
        case 'w': moveState.forward = false; break;
        case 's': moveState.backward = false; break;
        case 'a': moveState.left = false; break;
        case 'd': moveState.right = false; break;
    }
    
    // Update movement state in controller
    playerController.setMovementState(moveState);
});

// Pause state toggle function
function togglePauseState() {
    gameState.isPaused = !gameState.isPaused;
    const pauseMenu = document.getElementById('pauseMenu');
    const crosshair = document.getElementById('crosshair');
    const reactUI = document.getElementById('react-ui');
    const cameraTouchZone = document.getElementById('camera-touch-zone');

    if (gameState.isPaused) {
        // Pause game
        pauseMenu.style.display = 'flex';
        pauseMenu.style.zIndex = '2000'; // Bring pause menu to the very top
        crosshair.style.display = 'none';
        document.exitPointerLock();
        document.body.style.cursor = 'auto';
        if (reactUI) {
            reactUI.style.pointerEvents = 'auto';
            reactUI.style.cursor = 'auto';
        }
        // Disable camera touch zone interaction when paused
        if (cameraTouchZone) {
            cameraTouchZone.style.pointerEvents = 'none';
        }
    } else {
        // Resume game
        pauseMenu.style.display = 'none';
        pauseMenu.style.zIndex = ''; // Reset z-index
        if (gameState.mode === 'singleplayer' && gameState.hasGameStarted) {
            crosshair.style.display = 'block';
            requestGamePointerLock();
            if (document.pointerLockElement) {
                document.body.style.cursor = 'none';
            }
            if (reactUI) {
                reactUI.style.pointerEvents = 'none';
            }
            // Re-enable camera touch zone interaction when resumed
            if (cameraTouchZone) {
                cameraTouchZone.style.pointerEvents = 'auto';
            }
        }
    }
}

// Physics update variables
// We're using the clock defined at the top of the file 
// const clock = new THREE.Clock();

// Ensure player stays above terrain
function ensurePlayerAboveTerrain() {
    if (!playerMesh || !chunkManager) return;
    
    try {
        // Get terrain height at player position
        const terrainHeight = chunkManager.getHeightAt(
            playerMesh.position.x,
            playerMesh.position.z
        );
        
        // Ensure player is at least 1 unit above terrain
        const minHeightAboveTerrain = 1.0;
        if (playerMesh.position.y < terrainHeight + minHeightAboveTerrain) {
            playerMesh.position.y = terrainHeight + minHeightAboveTerrain;
            
            // Update physics body too
            if (playerBody) {
                playerBody.position.y = playerMesh.position.y;
            }
        }
    } catch (error) {
        console.warn("Error in ensurePlayerAboveTerrain:", error);
    }
}

// Create debug HUD
const debugHUD = document.createElement('div');
debugHUD.style.cssText = `
    position: fixed;
    top: 10px;
    left: 10px;
    color: white;
    font-family: Arial, sans-serif;
    font-size: 16px;
    text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.8);
    z-index: 9999;
    pointer-events: none;
    background-color: rgba(0, 0, 0, 0.7);
    padding: 8px 12px;
    border-radius: 4px;
    font-weight: bold;
`;
document.body.appendChild(debugHUD);

// Main game loop
function animate() {
    requestAnimationFrame(animate);
    
    // Update debug info
    const islandTypes = {
        0: "Circular Island",
        1: "Irregular Island",
        2: "Crescent Island",
        3: "Volcanic Island",
        4: "Atoll Island",
        5: "Archipelago"
    };
    const islandType = IslandTypeManager.getInstance().getType();
    const islandName = islandTypes[islandType] || "Unknown Island";
    const worldSeed = window.worldSeed || 'Unknown';
    debugHUD.textContent = `Island: ${islandName} | Seed: ${worldSeed}`;
    
    // Rest of animate function...
    const deltaTime = clock.getDelta();
    renderer.render(scene, camera);
    
    if (!gameInitialized || gameState.isPaused) return;
    
    // Update input adapter (mobile controls)
    if (inputAdapter) {
        inputAdapter.update();
    }

    // Update physics world
    world.step(1/60);

    // Update player movement using camera forward direction
    const cameraForward = new THREE.Vector3();
    camera.getWorldDirection(cameraForward);
    playerController.update(deltaTime, cameraForward);

    // Update continuous weapon firing
    if (isAttacking && playerController && playerController.inventory) {
        const currentWeapon = playerController.inventory.getEquipped();
        if (currentWeapon && typeof currentWeapon.fire === 'function') {
            currentWeapon.fire(playerController);
        }
    }

    // Update camera after player position is updated
    if (thirdPersonCamera) {
        thirdPersonCamera.update(chunkManager);
    }

    // Use new progressive loading system instead of the old chunk update
    if (gameLoader && playerController) {
        gameLoader.updateChunkLoading(
            playerController.getMesh().position,
            cameraForward
        );
    }
    
    // Update game systems
    zombieManager.updateZombies(playerController.getMesh());
    projectilePool.updateAll(deltaTime);

    // Safety position check to prevent clipping
    ensurePlayerAboveTerrain();
    
    // Update wave manager and power-up systems
    if (window.waveManager) {
        window.waveManager.update(deltaTime);
    }
    
    // Update powerup drops
    if (window.powerUpDropManager && window.playerMesh) {
        window.powerUpDropManager.update(window.playerMesh);
    }
}

// Handle shooting on click
renderer.domElement.addEventListener('click', (event) => {
    event.preventDefault();
    
    if (gameState.isPaused || gameState.mode === 'editor') return;

    // Ensure the document is focused
    if (!document.hasFocus()) {
        console.warn("Document is not focused. Cannot request pointer lock.");
        return;
    }

    // Only request pointer lock if game has officially started
    if (!document.pointerLockElement && 
        gameState.mode === 'singleplayer' && 
        gameState.hasGameStarted) {
        requestGamePointerLock();
        return;
    }
});

// Add continuous attack handling
let isAttacking = false;
let isAiming = false; // Track aiming state for right-click

// Handle mouse down for continuous attacks and aiming
renderer.domElement.addEventListener('mousedown', (event) => {
    if (gameState.isPaused || gameState.mode === 'editor') return;
    if (!document.pointerLockElement) return;

    // Left click - Start attacking state
    if (event.button === 0) {
        isAttacking = true;
        
        // Start firing weapon if it's a pistol
        if (playerController && playerController.inventory) {
            const currentWeapon = playerController.inventory.getEquipped();
            // Ensure the equipped item is a pistol and has the startFiring method
            if (currentWeapon && typeof currentWeapon.startFiring === 'function') {
                console.log("Starting weapon firing");
                currentWeapon.startFiring();
                currentWeapon.justPressed = true; // Ensure the pistol knows this is a new press
            }
        }
    }

    // Right click - handle aiming (only for pistol)
    if (event.button === 2) {
        isAiming = true;
        isPlayerAiming = true;
        updateCrosshair(true);
        if (playerController) {
            playerController.setAiming(true);
        }
    }
});

// Handle mouse up to stop attacks and aiming
renderer.domElement.addEventListener('mouseup', (event) => {
    // Left click release - stop attacking state
    if (event.button === 0) {
        isAttacking = false;
        
        // Stop firing weapon if it's a pistol
        if (playerController && playerController.inventory) {
            const currentWeapon = playerController.inventory.getEquipped();
            // Ensure the equipped item is a pistol and has the stopFiring method
            if (currentWeapon && typeof currentWeapon.stopFiring === 'function') {
                console.log("Stopping weapon firing");
                currentWeapon.stopFiring();
            }
        }
    }

    // Right click release - stop aiming
    if (event.button === 2) {
        isAiming = false;
        isPlayerAiming = false;
        updateCrosshair(false);
        if (playerController) {
            playerController.setAiming(false);
        }
    }
});

// Prevent right-click context menu
renderer.domElement.addEventListener('contextmenu', (event) => {
    event.preventDefault();
});

// Clear attack and aiming state when pointer lock is lost
document.addEventListener('pointerlockchange', () => {
    if (!document.pointerLockElement) {
        isAttacking = false;
        isAiming = false;
        isPlayerAiming = false;
        updateCrosshair(false);
        if (playerController) {
            playerController.setAiming(false);
        }
    }
});

// Move zombie spawning to an interval that only starts after initialization
let zombieSpawningStarted = false;

function startZombieSpawning() {
    // Disabled: We're now using the wave system instead
    /*
    if (zombieSpawningStarted) return;
    zombieSpawningStarted = true;
    
    // Spawn zombies every 5 seconds, but only if game is initialized
    setInterval(() => {
        if (gameInitialized && playerMesh) {
            zombieManager.spawnZombie(playerMesh);
        }
    }, 5000);
    */
    console.log('[Game] Automatic zombie spawning disabled - using wave system instead');
}

// Start zombie spawning a few seconds after initialization
setTimeout(() => {
    if (gameInitialized) {
        // Disabled - using wave system instead
        // startZombieSpawning();
    }
}, 5000);

function isWithinWorldBounds(x, z) {
    const worldSize = chunkManager.worldSize;
    const buffer = 10; // Stay away from absolute edge
    return x >= buffer && x < worldSize - buffer && 
           z >= buffer && z < worldSize - buffer;
}

// Handle window resizing
window.addEventListener('resize', () => {
    const width = window.innerWidth;
    const height = window.innerHeight;
    
    camera.aspect = width / height;
    camera.updateProjectionMatrix();
    
    if (worldEditorCamera) {
        worldEditorCamera.getCamera().aspect = width / height;
        worldEditorCamera.getCamera().updateProjectionMatrix();
    }
    
    renderer.setSize(width, height);
});

// Start the animation loop immediately
animate();

// Debug game state
console.log("--- DEBUG GAME STATE ---");
console.log("Game State:", gameState?.mode);
console.log("Game Started:", gameState?.hasGameStarted);
console.log("Player Controller:", !!playerController);
console.log("Player Model:", !!playerController?.playerModel);
console.log("Camera:", !!window.camera);
console.log("Scene:", !!scene);
console.log("GameState isPaused:", gameState?.isPaused);
console.log("----------------------");

// Debug commands for pistol leveling system
window.debugPistol = {
  addXP: (amount = 10) => {
    if (window.pistolSkill) {
      window.pistolSkill.debugAddXP(amount);
      return `Added ${amount} XP to pistol`;
    }
    return 'PistolSkill not available';
  },
  levelUp: () => {
    if (window.pistolSkill) {
      const newLevel = window.pistolSkill.debugLevelUp();
      return `Pistol leveled up to ${newLevel}`;
    }
    return 'PistolSkill not available';
  },
  getStats: () => {
    if (window.pistolSkill) {
      return window.pistolSkill.getStats();
    }
    return 'PistolSkill not available';
  },
  spawnZombie: () => {
    if (window.zombieManager && window.playerMesh) {
      window.zombieManager.spawnZombie(window.playerMesh);
      return 'Zombie spawned';
    }
    return 'ZombieManager or player not available';
  },
  spawnMultipleZombies: (count = 5) => {
    if (window.zombieManager && window.playerMesh) {
      for (let i = 0; i < count; i++) {
        window.zombieManager.spawnZombie(window.playerMesh);
      }
      return `${count} zombies spawned`;
    }
    return 'ZombieManager or player not available';
  }
};

// Debug commands for wave system
window.debugWave = {
  nextWave: () => {
    if (window.waveManager) {
      window.waveManager.completeWave();
      return `Completing current wave`;
    }
    return 'WaveManager not available';
  },
  startWave: () => {
    if (window.waveManager) {
      window.waveManager.startNextWave();
      return `Starting next wave`;
    }
    return 'WaveManager not available';
  },
  getCurrentWave: () => {
    if (window.waveManager) {
      return `Current wave: ${window.waveManager.getWave()}`;
    }
    return 'WaveManager not available';
  },
  spawnDrop: (tier = 'tier1') => {
    if (window.powerUpDropManager && window.playerMesh) {
      const position = window.playerMesh.position.clone();
      position.x += 2; // Spawn in front of player
      window.powerUpDropManager.createDrop(tier, position);
      return `Created ${tier} drop`;
    }
    return 'PowerUpDropManager or player not available';
  }
};

console.log('Pistol debug commands available: window.debugPistol.addXP(), levelUp(), getStats(), spawnZombie(), spawnMultipleZombies()');
console.log('Wave debug commands available: window.debugWave.nextWave(), startWave(), getCurrentWave(), spawnDrop()');
console.log('Debug UI available: Press Shift+P to toggle');

// Add event listeners for keyboard controls (if not already defined)
if (typeof initializeKeyboardControls !== 'function') {
    function initializeKeyboardControls() {
        document.addEventListener('keydown', (event) => {
            // Block editor hotkeys (usually Ctrl+E)
            if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'e') {
                console.log('Editor mode is temporarily disabled');
                event.preventDefault();
                return;
            }
            
            // Block directly switching to editor mode (if there's a key for it)
            if (event.key === 'F2' || event.key === 'F3') {
                console.log('Editor mode is temporarily disabled');
                event.preventDefault();
                return;
            }
        });
    }
    
    // Call this function on DOMContentLoaded
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initializeKeyboardControls);
    } else {
        initializeKeyboardControls();
    }
}

// Set a global flag to disable editor mode
window.editorModeDisabled = true;

// Add initialization to hide editor UI
document.addEventListener('DOMContentLoaded', () => {
    // Hide editor UI elements
    const editorElements = document.querySelectorAll('.editor-ui, #editorControls, [id*="editor"]');
    editorElements.forEach(el => {
        if (el) {
            console.log('Hiding editor element:', el.id || el.className);
            el.style.display = 'none';
        }
    });
    
    // Ensure we're in singleplayer mode
    if (typeof gameState !== 'undefined') {
        gameState.mode = 'singleplayer';
    }
});

// Disable editor UI click handlers
document.addEventListener('DOMContentLoaded', () => {
    // Find and disable editor mode button
    const editorModeButton = document.getElementById('editorMode');
    if (editorModeButton) {
        editorModeButton.addEventListener('click', (e) => {
            console.log('Editor mode is temporarily disabled');
            e.preventDefault();
            e.stopPropagation();
            return false;
        });
    }

    // Also disable any controls that would require editor mode
    const editorControls = document.querySelectorAll('[id*="editor"], [class*="editor"]');
    editorControls.forEach(control => {
        if (control) {
            control.disabled = true;
            control.title = 'Editor mode is temporarily disabled';
            
            // Intercept click events
            control.addEventListener('click', (e) => {
                console.log('Editor control is disabled');
                e.preventDefault();
                e.stopPropagation();
                return false;
            });
        }
    });
});

// Make togglePauseState available globally for mobile controls
window.togglePauseState = togglePauseState;
